import primitiveFactory from "_src/engine/factories/primitive";
import objFactory from "_src/engine/factories/object";
import textWidget from "_src/client/widgets/text"
import {primitiveType} from "_src/engine/constants";
import { screenUtils } from "_src/client/utils";
import { orthogonalization } from "_src/engine/math";

import LineShader from "_src/shaders/line/line";
import TextShader from "_src/shaders/text/text";

import { vec3, quat, mat4 } from "_src/libs/gl-matrix";

const LINES_COLOR = [0.4, 0.7, 0.9];

const DL_LINEAR  = 0;
const DL_ANGULAR = 1;

export default {

  DL_LINEAR,
  DL_ANGULAR,

  create(type, params = {}) {
    switch (type) {
      case DL_LINEAR:
        return createLinearDimension(params);
      case DL_ANGULAR:
        return createAngularDimension(params);
    }
  },
}

function createCadSpaceMat(lpoint, rpoint) {
  const transform = mat4.create();
  const origin = vec3.create();

  const axis = vec3.sub(vec3.create(), lpoint, rpoint);
  vec3.scaleAndAdd(origin, rpoint, axis, 0.5);
  vec3.normalize(axis, axis);

  const up = vec3.create();
  const right = vec3.create();
  orthogonalization(axis, right, up);

  const rotation = quat.setAxes(quat.create(), up, right, axis);
  quat.conjugate(rotation, rotation);
  mat4.fromRotationTranslation(transform, rotation, origin);

  return transform;
}

function createLinearDimension({lpoint, rpoint, offset, text}) {
  const cadSpaceMat = createCadSpaceMat(lpoint, rpoint);

  const pixelRatio = screenUtils.getPixelRatio();
  const lineShader = new LineShader({ lineWidth : 2 * pixelRatio });
  const linesDrawable = objFactory.createDrawable(lineShader);

  const lengthX = vec3.distance(lpoint, rpoint);

  const rline = objFactory.createDrawable(lineShader);
  const rlineChunk = primitiveFactory.create(primitiveType.LINE,
    {x0: 0, y0 : -0.5 * lengthX, z0 : 0.0, x1: 0, y1 : -0.5 * lengthX, z1 : offset});
  rline.addGeometryChunk(rlineChunk);
  linesDrawable.add(rline);

  const lline = objFactory.createDrawable(lineShader);
  const llineChunk = primitiveFactory.create(primitiveType.LINE,
    {x0: 0, y0 : 0.5 * lengthX, z0 : 0.0, x1: 0, y1 : 0.5 * lengthX, z1 : offset});
  lline.addGeometryChunk(llineChunk);
  linesDrawable.add(lline);

  const dline = objFactory.createDrawable(lineShader);
  const dlineChunk = primitiveFactory.create(primitiveType.LINE,
    {x0: 0, y0 : -0.5 * lengthX, z0 : offset, x1: 0, y1 : 0.5 * lengthX, z1 : offset});
  dline.addGeometryChunk(dlineChunk);
  linesDrawable.add(dline);

  rline.setMatrix(cadSpaceMat);
  lline.setMatrix(cadSpaceMat);
  dline.setMatrix(cadSpaceMat);

  rline.setFrustrumCulling(false);
  lline.setFrustrumCulling(false);
  dline.setFrustrumCulling(false);

  rline.setDiffuseColor(...LINES_COLOR);
  lline.setDiffuseColor(...LINES_COLOR);
  dline.setDiffuseColor(...LINES_COLOR);

  const textRotation = quat.create();
  quat.rotateY(textRotation, textRotation, -0.5 * Math.PI);
  quat.rotateZ(textRotation, textRotation, -0.5 * Math.PI);
  const textPosition = [0,0,offset];

  const textShader = new TextShader({transparent : true});
  const textDrawableInner = textWidget.create(text, textShader);

  const textDrawable = objFactory.createDrawable(textShader);
  const textLocalMat = mat4.fromRotationTranslation(mat4.create(), textRotation, textPosition);
  mat4.mul(textLocalMat, cadSpaceMat, textLocalMat);
  textDrawable.add(textDrawableInner);
  textDrawableInner.setMatrix(textLocalMat);

  return [linesDrawable, textDrawable];
}

function createAngularDimension({lpoint, rpoint, offset, text, angle, idx}) {
  const cadSpaceMat = createCadSpaceMat(lpoint, rpoint);

  const pixelRatio = screenUtils.getPixelRatio();
  const lineShader = new LineShader({ lineWidth : 2 * pixelRatio });
  const linesDrawable = objFactory.createDrawable(lineShader);

  const lengthX = vec3.distance(lpoint, rpoint);
  const posY = idx === 0 ? 0.5 * lengthX : -0.5 * lengthX;
  const hlineOffset = idx === 0 ? -offset * Math.tan(angle) : offset * Math.tan(angle);
  const hlineEnd = idx === 0 ? -offset : offset;

  const vline = objFactory.createDrawable(lineShader);
  const vlineChunk = primitiveFactory.create(primitiveType.LINE,
    {x0: 0, y0 : posY, z0 : 0.0, x1: hlineOffset, y1 : posY, z1 : offset}
  );
  vline.addGeometryChunk(vlineChunk);
  linesDrawable.add(vline);

  const hline = objFactory.createDrawable(lineShader);
  const hlineChunk = primitiveFactory.create(primitiveType.LINE,
    {x0 : hlineOffset, y0 : posY, z0 : offset, x1: hlineEnd, y1 : posY, z1 : offset}
  );
  hline.addGeometryChunk(hlineChunk);
  linesDrawable.add(hline);

  vline.setMatrix(cadSpaceMat);
  hline.setMatrix(cadSpaceMat);

  vline.setFrustrumCulling(false);
  hline.setFrustrumCulling(false);

  vline.setDiffuseColor(...LINES_COLOR);
  hline.setDiffuseColor(...LINES_COLOR);

  const textPosition = [(hlineOffset + hlineEnd) / 2, posY, offset];
  const textRotation = quat.create();
  quat.rotateX(textRotation, textRotation, 0.5 * Math.PI);

  const textShader = new TextShader({transparent : true});
  const textDrawableInner = textWidget.create(text, textShader);

  const textDrawable = objFactory.createDrawable(textShader);
  const textLocalMat = mat4.fromRotationTranslation(mat4.create(), textRotation, textPosition);
  mat4.mul(textLocalMat, cadSpaceMat, textLocalMat);
  textDrawable.add(textDrawableInner);
  textDrawableInner.setMatrix(textLocalMat);

  return [linesDrawable, textDrawable];

}
